import { TitleCasePipe } from '@angular/common'
import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { RouterLink } from '@angular/router'
import { CourtSystemWithMetadata } from '@ftr/contracts/api/court-system'
import { TRANSCRIBER_PERMISSIONS } from '@ftr/contracts/api/user-group'
import { ProductionStatus } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { IconButtonComponent, IconComponent, unwrapData } from '@ftr/foundation'
import { DropdownMenuLegacyComponent, StaticMenuItem } from '@ftr/menus'
import { AppPaths } from '@ftr/routing-paths'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { map } from 'rxjs'
import { COURT_SYSTEM_NAV_ITEMS, PUBLIC_COURT_SYSTEM_NAV_ITEMS, SideNavDisplayMode } from '../../navigation'
import { LogoSize, LogoTheme } from '../../types'
import { FtrLogoComponent } from '../ftr-logo'

@Component({
  selector: 'ftr-court-system-dropdown',
  templateUrl: './court-system-dropdown.component.html',
  styleUrls: ['./court-system-dropdown.component.css'],
  encapsulation: ViewEncapsulation.None,
  host: {
    '[attr.theme]': 'theme()',
  },
  imports: [
    DropdownMenuLegacyComponent,
    TitleCasePipe,
    RouterLink,
    FtrLogoComponent,
    IconComponent,
    IconButtonComponent,
  ],
})
export class CourtSystemDropdownComponent {
  private readonly store = inject(Store)
  private readonly launchDarklyService = inject(LaunchDarklyService)

  currentCourtSystemId = input<Uuid | undefined>()
  theme = input<'light' | 'dark'>('dark')

  @Input() collapsed: boolean
  @Input() displayMode: SideNavDisplayMode = 'DEFAULT'
  @Output() dropdownOpened = new EventEmitter<CourtSystemWithMetadata | undefined>()

  protected courtSystem: CourtSystemWithMetadata | undefined

  protected courtSystemMenuItems = computed<StaticMenuItem[]>(() => {
    if (this.publicNavigationEnabled()) {
      return PUBLIC_COURT_SYSTEM_NAV_ITEMS
    }
    return this.currentCourtSystemId() ? COURT_SYSTEM_NAV_ITEMS : COURT_SYSTEM_NAV_ITEMS.slice(0, 1)
  })

  // User are not court members if they only have transcriber permissions
  protected isCourtSystemMember = toSignal(
    this.store.select(UserState.hasOnlyPermissionsInAllCourtSystems).pipe(map(fn => !fn(...TRANSCRIBER_PERMISSIONS))),
  )

  protected publicNavigationEnabled = toSignal(this.launchDarklyService.observeFlagChanges('public-navigation'))

  @ViewChild(DropdownMenuLegacyComponent) protected dropdown: DropdownMenuLegacyComponent

  protected readonly productionStatus = ProductionStatus
  protected readonly appPaths = AppPaths
  protected readonly logoSize = LogoSize
  protected readonly logoTheme = LogoTheme
  protected readonly componentSelector = 'ftr-court-system-dropdown'

  readonly #destroyRef = inject(DestroyRef)

  constructor() {
    effect(() => {
      const courtSystemId = this.currentCourtSystemId()
      this.syncCourtSystem(courtSystemId)
    })
  }

  toggle(): void {
    this.dropdown.toggle()
  }

  private syncCourtSystem(courtSystemId: Uuid | undefined): void {
    this.store
      .select(UserState.courtSystems)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        unwrapData(),
        map(courtSystems => courtSystems.find(c => c.id === courtSystemId) as CourtSystemWithMetadata),
      )
      .subscribe(courtSystem => (this.courtSystem = courtSystem))
  }
}
